import {Component} from '@angular/core';
import {ButtonComponent} from "../../components/ui/button/button.component";
import {CardComponent} from "../../components/ui/card/card.component";
import {InputComponent} from "../../components/ui/input/input.component";
import {TabComponent} from "../../components/ui/tab/tab.component";
import {Column, TableComponent} from "../../components/ui/table/table.component";
import {TabsComponent} from "../../components/ui/tabs/tabs.component";
import {TitleComponent} from "../../components/ui/title/title.component";
import {TranslateModule} from "@ngx-translate/core";
import {ActivatedRoute, RouterOutlet} from "@angular/router";
import {DetailEntryFieldComponent} from "../../components/ui/detail-entry-field/detail-entry-field.component";
import {BehaviorSubject, combineLatest, firstValueFrom, map, shareReplay, switchMap} from "rxjs";
import {AsyncPipe, DatePipe, NgIf} from "@angular/common";
import {AssignmentService} from "../../services/assignment.service";
import {ProjectRole} from "../../enum/project-role";
import {FormControl, ReactiveFormsModule, Validators} from "@angular/forms";
import {ModalService} from "../../services/modal.service";
import {Assignment} from "../../models/assignment";
import {DeleteConfirmModalComponent} from "../../components/modals/delete-confirm-modal/delete-confirm-modal.component";
import {AuthenticationService} from "../../services/authentication.service";
import {ProjectAdminAccessDirective} from "../../directives/project-admin-access.directive";

@Component({
  selector: 'app-project-detail-info',
  standalone: true,
  imports: [
    ButtonComponent,
    CardComponent,
    InputComponent,
    TabComponent,
    TableComponent,
    TabsComponent,
    TitleComponent,
    TranslateModule,
    RouterOutlet,
    DetailEntryFieldComponent,
    AsyncPipe,
    DatePipe,
    ReactiveFormsModule,
    NgIf,
    ProjectAdminAccessDirective
  ],
  templateUrl: './project-detail-info.component.html',
  styleUrl: './project-detail-info.component.scss'
})
export class ProjectDetailInfoComponent {
  refresh$ = new BehaviorSubject(null);
  project$ = this.route.parent!.data.pipe(map(data => data['project']));
  assignments$ = combineLatest([this.project$, this.refresh$]).pipe(
    switchMap(([project]) => this.assignmentService.list(project)),
    shareReplay(1),
  )

  userAssignments$ = this.assignments$.pipe(
    map(assignments => assignments.filter(assignment => assignment.role === ProjectRole.USER)),
  );

  adminAssignments$ = this.assignments$.pipe(
    map(assignments => assignments.filter(assignment => assignment.role === ProjectRole.ADMIN)),
  );

  isProjectAdmin$ = this.project$.pipe(map(project => this.authenticationService.isProjectAdmin(project)));

  public columns: Column[] = [
    { name: 'user.email', label: 'page.project-detail-info.user-list.email' },
    { name: 'invitedAt', label: 'page.project-detail-info.user-list.invited-at', date: true },
  ];

  invitingAdmin = false;
  invitingAdminError = false;
  invitingAdminSuccess = false;
  invitingAdminControl = new FormControl('', { validators: [Validators.required, Validators.email] });

  invitingUser = false;
  invitingUserError = false;
  invitingUserSuccess = false;
  invitingUserControl = new FormControl('', { validators: [Validators.required, Validators.email] });

  constructor(private route: ActivatedRoute, private authenticationService: AuthenticationService, private assignmentService: AssignmentService, private modalService: ModalService) {
  }

  async inviteAdmin() {
    if (!this.invitingAdminControl.valid || this.invitingAdmin) {
      return;
    }

    this.invitingAdmin = true;
    this.invitingAdminError = false;
    this.invitingAdminSuccess = false;

    try {
      const email = this.invitingAdminControl.value!;
      const project = await firstValueFrom(this.project$);

      await firstValueFrom(this.assignmentService.invite(project, email, ProjectRole.ADMIN));

      this.invitingAdminSuccess = true;
      this.refresh$.next(null);
      this.invitingAdminControl.reset();
    } catch (e) {
      this.invitingAdminError = true;
    } finally {
      this.invitingAdmin = false;
    }
  }

  async inviteUser() {
    if (!this.invitingUserControl.valid || this.invitingUser) {
      return;
    }

    this.invitingUser = true;
    this.invitingUserError = false;
    this.invitingUserSuccess = false;

    try {
      const email = this.invitingUserControl.value!;
      const project = await firstValueFrom(this.project$);

      await firstValueFrom(this.assignmentService.invite(project, email, ProjectRole.USER));

      this.invitingUserSuccess = true;
      this.refresh$.next(null);
      this.invitingUserControl.reset();
    } catch (e) {
      this.invitingUserError = true;
    } finally {
      this.invitingUser = false;
    }
  }

  deleteAssignment(assignment: Assignment) {
    this.modalService.open(DeleteConfirmModalComponent, {
      data: {
        type: 'page.project-detail-info.delete-assignment.entity',
        name: assignment.user.email,
        confirm: async () => {
          await firstValueFrom(this.assignmentService.delete(assignment));
          this.refresh$.next(null);
        },
      }
    });
  }
}
